import {
  FieldSet,
  FormattedString,
  FormContext,
  FormState,
  InputWizardPanelComponent,
  IpInput,
  NetworkAddressInput,
  SelectComponent,
  TextInput,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { McvcnSelect } from "../../components/McvcnSelect/McvcnSelect";
import { VNETSelect } from "../../components/VNETSelect/VNETSelect";
import { networkCidrSizingUrl, vnetTroubleshooting } from "../../constants/docConstants";
import { AzureVirtualNetworkSummary } from "../../gen/clients/mchub-azure-api-client";
import { IdResourceType } from "../../helpers/idHelper";
import { FutureMcvcnExisting, FutureMcvcnNew, maxNetworkLinkCidrPrefix } from "../../helpers/resourceHelper";
import {
  alphaNumericHyphenRegex,
  startWithLetterRegex,
  validateNetworkLinkAddressSpace,
  vmClusterHostnameMaxLength,
} from "../../helpers/validationHelper";
import { useFeatures } from "../../hooks/useFeatures";
import { useOperation } from "../../hooks/useOperation";
import { McvcnCreateNewProps, newMcvcnCreateNew } from "../../operations/Mcvcn/McvcnCreateNew";

export const GROUP_NETWORK = "network";

export enum FieldTestIds {
  Hostname = "hostName",
  Vnet = "vnet",
  NetworkVirtualAppliance = "networkVirtualAppliance",
  Cidrs = "cidrs",
  Mcvcn = "virtualCloudNetworkSelect",
}

export enum LinkTestIds {
  VirtualCloudNetwork = "virtualCloudNetworkLink",
}

export enum Fields {
  Cidrs = "networkLinkDetails.ociNetworkCidrs",
  Vnet = "networkLinkDetails.azureAttachedNetworkIds",
  Hostname = "cloudVmClusterDetails.hostname",
  Mcvcn = "multiCloudVirtualNetworkDetails.id",
  NetworkVirtualAppliance = "networkLinkDetails.customerNvaIpAddress",
}

export interface NetworkTabContentProps {
  inputWizardPanelRef: InputWizardPanelComponent;
  subscriptionId: string;
  location: string;
}

export const NetworkTabContent = ({
  inputWizardPanelRef,
  subscriptionId,
  location,
}: NetworkTabContentProps): JSX.Element => {
  const { enableMcvcn, enableExaMcvcnCreate } = useFeatures();
  const [selectedVnet, setSelectedVnet] = React.useState<AzureVirtualNetworkSummary | undefined>(undefined);
  const [selectRef, setSelectRef] = React.useState<SelectComponent>();
  const [renderKey, setRenderKey] = React.useState<string>(uniqueGUID());

  const form: FormState = React.useContext(FormContext);

  const onError = (): void => inputWizardPanelRef.showError(Messages.createCommon.loadingErrors.general());
  const onMissingDependencies = (missingDependencies?: IdResourceType[]): void => {
    if (missingDependencies?.includes(IdResourceType.VNETS)) {
      inputWizardPanelRef.showErrorDialog(
        Messages.createCommon.dependencyWarnings.virtualNetworkNotSelected.message(),
        Messages.createCommon.dependencyWarnings.virtualNetworkNotSelected.title(),
      );
    } else {
      inputWizardPanelRef.showErrorDialog(
        Messages.createCommon.dependencyWarnings.basicsIncomplete.message(),
        Messages.createCommon.dependencyWarnings.basicsIncomplete.title(),
      );
    }
  };

  const onChangeVnet = (_: string, vnet: AzureVirtualNetworkSummary | undefined): void => {
    form.setValue(undefined, Fields.Cidrs, GROUP_NETWORK);
    setSelectedVnet(vnet);
    setRenderKey(uniqueGUID());
  };

  const { trigger: triggerMcvcnCreateNew } = useOperation<McvcnCreateNewProps>(newMcvcnCreateNew);

  const onLinkClick = (): void => {
    const missingDependencies: IdResourceType[] = [];
    if (!subscriptionId) missingDependencies.push(IdResourceType.SUBSCRIPTIONS);
    else if (!location) missingDependencies.push(IdResourceType.LOCATION);

    if (missingDependencies.length > 0) {
      onMissingDependencies?.(missingDependencies);
      return;
    }

    triggerMcvcnCreateNew({
      location,
      subscriptionId,
      onExecute: (futureMcvcn: FutureMcvcnExisting | FutureMcvcnNew): void => {
        selectRef?.setOptionNew(futureMcvcn.name, futureMcvcn);
      },
    });
  };

  const createVcnLink = {
    id: "create_new_vcn",
    text: Messages.actions.createNew(),
    onLinkClick,
    testId: LinkTestIds.VirtualCloudNetwork,
  };

  return (
    <>
      <FieldSet header={Messages.createExaDb.networkingTab.sectionTitles.vmClusterNetworking()}>
        <TextInput
          required
          fieldName={Fields.Hostname}
          testId={FieldTestIds.Hostname}
          label={Messages.labels.hostName()}
          validator={(value: string | undefined) => {
            const errors: string[] = [];
            if (value && !startWithLetterRegex.test(value)) {
              errors.push(Messages.validation.nameStartChar());
            }
            if (value && !alphaNumericHyphenRegex.test(value)) {
              errors.push(Messages.validation.valueAlphaNumericHyphen());
            }
            if (value && value.length > vmClusterHostnameMaxLength) {
              errors.push(Messages.validation.valueMaxLen(vmClusterHostnameMaxLength.toString()));
            }
            return errors.length ? errors : undefined;
          }}
        />
      </FieldSet>
      <FieldSet header={Messages.createExaDb.networkingTab.sectionTitles.networkPeering()}>
        {!(enableMcvcn && enableExaMcvcnCreate) && (
          <>
            <VNETSelect
              required
              testId={FieldTestIds.Vnet}
              fieldName={Fields.Vnet}
              label={Messages.labels.virtualNetwork()}
              tooltip={
                FormattedString(
                  { inputText: Messages.hints.tooltipVNET(vnetTroubleshooting) },
                ) as unknown as string
              }
              subscriptionId={subscriptionId}
              location={location}
              onChange={onChangeVnet}
              onError={onError}
              onMissingDependencies={onMissingDependencies}
            />
            <IpInput
              fieldName={Fields.NetworkVirtualAppliance}
              testId={FieldTestIds.NetworkVirtualAppliance}
              label={Messages.labels.networkVirtualAppliance()}
              tooltip={Messages.hints.tooltipNetworkVirtualAppliance()}
              validation={{
                containsIpAddress: true,
                containsCidrBlock: false,
              }}
            />
            <NetworkAddressInput
              required
              key={renderKey}
              fieldName={Fields.Cidrs}
              testId={FieldTestIds.Cidrs}
              groupName={GROUP_NETWORK}
              label={Messages.labels.ociCidr()}
              tooltip={FormattedString(
                { inputText: Messages.hints.toolTipCidr(networkCidrSizingUrl) },
              ) as unknown as string}
              validator={validateNetworkLinkAddressSpace(maxNetworkLinkCidrPrefix, selectedVnet?.addressSpaces)}
            />
          </>
        )}
        {enableMcvcn && enableExaMcvcnCreate && (
          <McvcnSelect
            required
            testId={FieldTestIds.Mcvcn}
            label={Messages.labels.virtualCloudNetwork()}
            componentRef={setSelectRef}
            location={location}
            fieldName={Fields.Mcvcn}
            groupName={GROUP_NETWORK}
            subscriptionId={subscriptionId}
            inputLink={createVcnLink}
            onError={onError}
            onMissingDependencies={onMissingDependencies}
          />
        )}
      </FieldSet>
    </>
  );
};
